/**
 * The email layout: the branded chrome every message this platform sends is poured into.
 *
 * ## What lives here, and what deliberately does not
 *
 * The logo image, the platform name, the support address and the website are **not** edited
 * here. They live on `platform_settings` and are edited on Business settings, and duplicating a
 * second way to write them is how two screens end up disagreeing about what the platform is
 * called. This panel owns the chrome: the sender name, the reply-to, whether the logo shows and
 * how wide, the eleven palette colours, the footer text, the legal line, and the two link rows.
 *
 * ## Why saving makes a version rather than an edit
 *
 * The API stores layouts append-only: a save inserts a version and moves the active marker. That
 * matters because this is the one screen whose mistakes are invisible to the person making them
 * and visible to everyone else. An operator who picks a palette that looks fine on their monitor
 * and unreadable in a dark mail client needs one click back, not a colour-matching exercise from
 * memory. The history panel is the other half of that promise.
 *
 * ## The colour guard
 *
 * `checkEmailPalette` is the console's copy of the contract's rule, and `emailLayout.test.ts`
 * pins it against the real thing by source path. Every pair where one colour is text drawn on
 * another must clear WCAG AA at 4.5:1, and the save is refused otherwise. This is not advisory:
 * the API refuses the same palette through its schema, so a console that let it through would
 * only be choosing where the operator finds out.
 */
import { useMemo, useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import type {
  EmailLayout,
  EmailLayoutContent,
  EmailLayoutVersion,
  EmailPaletteField,
} from '../contracts/types.js';
import { normaliseHex } from '../lib/bannerContrast.js';
import {
  DEFAULT_EMAIL_PALETTE,
  EMAIL_LOGO_MAX_WIDTH,
  EMAIL_LOGO_MIN_WIDTH,
  PALETTE_FIELDS,
  checkEmailPalette,
  defaultEmailLayoutContent,
  normaliseEmailLinks,
} from '../lib/emailLayout.js';
import { Badge, Button, Card, Field, Input, Switch, Textarea } from './ui.js';
import { IconRefresh, IconX } from './icons.js';
import { useToast } from './Toast.js';

type LinkRow = { label: string; url: string };

const SOCIAL_MAX = 8;
const LEGAL_MAX = 6;

export function EmailLayoutEditor({
  layout,
  versions,
  canWrite,
  onSaved,
  onVersions,
  onDraft,
}: {
  /** The active layout, or null when this deployment has never written one. */
  layout: EmailLayout | null;
  versions: readonly EmailLayoutVersion[];
  canWrite: boolean;
  onSaved: (next: EmailLayout) => void;
  onVersions: (next: readonly EmailLayoutVersion[]) => void;
  /** Every keystroke, so the preview beside this panel is the layout being typed. */
  onDraft: (content: EmailLayoutContent) => void;
}): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();

  const initial = layout?.content ?? defaultEmailLayoutContent();
  const [content, setContent] = useState<EmailLayoutContent>(initial);
  const [socialRows, setSocialRows] = useState<LinkRow[]>(() =>
    initial.socialLinks.map((l) => ({ label: l.label, url: l.url })),
  );
  const [legalRows, setLegalRows] = useState<LinkRow[]>(() =>
    initial.legalLinks.map((l) => ({ label: l.label, url: l.url })),
  );
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState<number | null>(null);
  const [problems, setProblems] = useState<readonly string[]>([]);

  const verdict = useMemo(() => checkEmailPalette(content), [content]);

  /**
   * Apply one change and tell the preview about it.
   *
   * The merge happens here rather than inside the state updater on purpose. A `setState`
   * updater has to be pure: React may call it twice, and it does in StrictMode, so notifying the
   * parent from inside one would fire the preview update twice per keystroke. Merging against
   * `content` is safe because every caller is a change handler, one edit per event.
   */
  function patch(next: Partial<EmailLayoutContent>): void {
    const merged: EmailLayoutContent = { ...content, ...next };
    setContent(merged);
    onDraft(merged);
  }

  function setColour(field: EmailPaletteField, value: string): void {
    patch({ [field]: value } as Partial<EmailLayoutContent>);
  }

  async function save(): Promise<void> {
    setProblems([]);

    if (!verdict.ok) {
      setProblems(verdict.failures.map((f) => f.message));
      return;
    }
    const social = normaliseEmailLinks(socialRows, SOCIAL_MAX, 'social links');
    if ('errors' in social) {
      setProblems(social.errors);
      return;
    }
    const legal = normaliseEmailLinks(legalRows, LEGAL_MAX, 'legal links');
    if ('errors' in legal) {
      setProblems(legal.errors);
      return;
    }
    // Copied rather than passed through: `EmailLayoutContent` declares mutable arrays and the
    // validator hands back readonly ones, which is the safer shape for it to return.
    const socialLinks = [...social.links];
    const legalLinks = [...legal.links];
    const replyTo = content.replyTo?.trim() ?? '';
    if (replyTo !== '' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(replyTo)) {
      setProblems(['The reply-to address is not a valid email address. Leave it empty to use the from address.']);
      return;
    }

    const body: EmailLayoutContent = {
      ...content,
      senderName: content.senderName.trim(),
      replyTo: replyTo === '' ? null : replyTo,
      footerText: content.footerText.trim(),
      footerLegalText: content.footerLegalText.trim(),
      socialLinks,
      legalLinks,
    };

    setSaving(true);
    try {
      const next = await api.updateEmailLayout(body);
      onSaved(next);
      setContent(next.content);
      onDraft(next.content);
      try {
        const history = await api.emailLayoutVersions();
        onVersions(history.versions);
      } catch {
        // The save landed; the history is a nicety. Failing to refresh it is not worth a red
        // box over a change that worked.
      }
      toast.success('Layout saved', `Version ${String(next.version)} is now the live one.`);
    } catch (err) {
      toast.error('Could not save the layout', messageOf(err));
    } finally {
      setSaving(false);
    }
  }

  async function restore(version: number): Promise<void> {
    if (restoring !== null) return;
    setRestoring(version);
    try {
      const next = await api.restoreEmailLayout({ version });
      onSaved(next);
      setContent(next.content);
      setSocialRows(next.content.socialLinks.map((l) => ({ label: l.label, url: l.url })));
      setLegalRows(next.content.legalLinks.map((l) => ({ label: l.label, url: l.url })));
      onDraft(next.content);
      const history = await api.emailLayoutVersions();
      onVersions(history.versions);
      toast.success('Layout restored', `Version ${String(version)} is live again as version ${String(next.version)}.`);
    } catch (err) {
      toast.error('Could not restore that version', messageOf(err));
    } finally {
      setRestoring(null);
    }
  }

  return (
    <>
      <Card
        title="Layout"
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {layout !== null && (
              <span className="faint" style={{ fontSize: 12 }}>
                Version {layout.version}
              </span>
            )}
            <Button variant="primary" size="sm" loading={saving} disabled={!canWrite} onClick={() => void save()}>
              Save layout
            </Button>
          </div>
        }
      >
        {layout === null && (
          <div className="mailnote" role="status">
            No layout has been saved yet, so every message uses the shipped default. Saving here
            makes it yours.
          </div>
        )}

        <div className="grid grid--2" style={{ gap: 14 }}>
          <Field label="Sender name" htmlFor="lay-sender">
            <Input
              id="lay-sender"
              value={content.senderName}
              maxLength={120}
              disabled={!canWrite}
              placeholder="The name customers see in their inbox"
              onChange={(e) => patch({ senderName: e.target.value })}
            />
            <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
              Shown beside the from address. Leave it empty to send the bare address.
            </div>
          </Field>
          <Field label="Reply-to address (optional)" htmlFor="lay-replyto">
            <Input
              id="lay-replyto"
              type="email"
              value={content.replyTo ?? ''}
              disabled={!canWrite}
              placeholder="support@yourdomain.com"
              onChange={(e) => patch({ replyTo: e.target.value === '' ? null : e.target.value })}
            />
            <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
              Where a reply lands. Empty leaves the header off entirely rather than sending an
              empty one.
            </div>
          </Field>
        </div>

        <div className="grid grid--2" style={{ gap: 14 }}>
          <div className="field">
            <span className="field__label">Show the logo</span>
            <div className="setting-row">
              <span className="faint" style={{ fontSize: 13 }}>
                {content.showLogo
                  ? 'The header band shows your logo.'
                  : 'The header band shows the platform name instead.'}
              </span>
              <Switch
                checked={content.showLogo}
                disabled={!canWrite}
                label="Show the logo in the header"
                onChange={(showLogo) => patch({ showLogo })}
              />
            </div>
          </div>
          <Field label={`Logo width (${String(EMAIL_LOGO_MIN_WIDTH)} to ${String(EMAIL_LOGO_MAX_WIDTH)} px)`} htmlFor="lay-logow">
            <Input
              id="lay-logow"
              type="number"
              min={EMAIL_LOGO_MIN_WIDTH}
              max={EMAIL_LOGO_MAX_WIDTH}
              value={String(content.logoWidthPx)}
              disabled={!canWrite || !content.showLogo}
              className="mono"
              onChange={(e) => {
                const next = Number(e.target.value);
                if (Number.isFinite(next)) patch({ logoWidthPx: Math.round(next) });
              }}
            />
          </Field>
        </div>

        <div className="field">
          <span className="field__label">Colours</span>
          <p className="faint" style={{ fontSize: 12, marginTop: -2 }}>
            Every colour that carries text is checked against the one it is drawn on. A pair that
            cannot be read is refused here and by the API, so nobody can ship a message their
            customers cannot see.
          </p>
          <div className="palette-grid">
            {PALETTE_FIELDS.map((entry) => (
              <ColourField
                key={entry.field}
                label={entry.label}
                hint={entry.hint}
                id={`lay-${entry.field}`}
                value={content[entry.field]}
                disabled={!canWrite}
                bad={verdict.failures.some((f) => f.field === entry.field)}
                onChange={(value) => setColour(entry.field, value)}
              />
            ))}
          </div>
          <Button
            size="sm"
            variant="ghost"
            disabled={!canWrite}
            onClick={() => patch({ ...DEFAULT_EMAIL_PALETTE })}
          >
            <IconRefresh size={14} /> Back to the shipped colours
          </Button>
        </div>

        <PaletteVerdict verdict={verdict} />

        <Field label="Footer text" htmlFor="lay-footer">
          <Textarea
            id="lay-footer"
            value={content.footerText}
            rows={2}
            maxLength={600}
            disabled={!canWrite}
            placeholder="The courtesy line under every message"
            onChange={(e) => patch({ footerText: e.target.value })}
          />
        </Field>

        <Field label="Legal line" htmlFor="lay-legal">
          <Textarea
            id="lay-legal"
            value={content.footerLegalText}
            rows={2}
            maxLength={600}
            disabled={!canWrite}
            placeholder="Registered name, address, licence number"
            onChange={(e) => patch({ footerLegalText: e.target.value })}
          />
          <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
            Kept apart from the footer text because one is a courtesy sentence and the other is a
            disclosure a regulator may require word for word.
          </div>
        </Field>

        <LinkRows
          title="Social links"
          max={SOCIAL_MAX}
          rows={socialRows}
          disabled={!canWrite}
          onChange={setSocialRows}
        />
        <LinkRows
          title="Legal links"
          max={LEGAL_MAX}
          rows={legalRows}
          disabled={!canWrite}
          onChange={setLegalRows}
        />

        {problems.length > 0 && (
          <div className="form-error" role="alert">
            <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </div>
        )}

        {!canWrite && (
          <div className="muted" style={{ fontSize: 13 }}>
            Your role can view but not change the layout.
          </div>
        )}
      </Card>

      {versions.length > 0 && (
        <Card title="Earlier versions">
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            Saving never overwrites. Restoring copies an earlier version forward as a new one, so
            nothing is lost either way.
          </p>
          <ul className="layout-versions">
            {versions.map((entry) => (
              <li key={entry.version}>
                <span className="layout-versions__num mono">v{entry.version}</span>
                <span className="layout-versions__when faint">
                  {new Date(entry.updatedAt).toLocaleString()}
                </span>
                {entry.active ? (
                  <Badge tone="green" dot>
                    Live
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={restoring === entry.version}
                    disabled={!canWrite || restoring !== null}
                    onClick={() => void restore(entry.version)}
                  >
                    Restore
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

/**
 * The verdict panel.
 *
 * Shows every ratio, not only the failures, because an operator a tenth of a point from failing
 * should be able to see that they are rather than discover it on the next tweak.
 */
function PaletteVerdict({
  verdict,
}: {
  verdict: ReturnType<typeof checkEmailPalette>;
}): JSX.Element {
  return (
    <div
      className={`banner-verdict ${verdict.ok ? 'banner-verdict--ok' : 'banner-verdict--bad'}`}
      role={verdict.ok ? undefined : 'alert'}
    >
      <div className="banner-verdict__head">
        {verdict.ok
          ? 'Every piece of text on this layout is readable on what it sits on.'
          : 'These colours cannot be saved yet.'}
      </div>
      {!verdict.ok && (
        <ul className="banner-verdict__list">
          {verdict.failures.map((failure, index) => (
            <li key={`${failure.field}-${String(index)}`}>{failure.message}</li>
          ))}
        </ul>
      )}
      <div className="banner-verdict__ratios">
        {verdict.ratios.map((row, index) => (
          <span key={`${row.field}-${String(index)}`}>
            {row.label} {row.ratio ?? '-'}:1
          </span>
        ))}
      </div>
    </div>
  );
}

/** A colour, as a swatch and a hex box, matching the banner editor's control exactly. */
function ColourField({
  label,
  hint,
  id,
  value,
  disabled,
  bad,
  onChange,
}: {
  label: string;
  hint: string;
  id: string;
  value: string;
  disabled: boolean;
  bad: boolean;
  onChange: (next: string) => void;
}): JSX.Element {
  // `<input type="color">` silently resets anything that is not six hex digits, which would
  // fight an operator halfway through typing one.
  const swatch = normaliseHex(value) ?? '#000000';
  return (
    <div className={`field palette-field${bad ? ' palette-field--bad' : ''}`}>
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <div className="color-field">
        <input
          type="color"
          className="color-field__swatch"
          value={swatch}
          disabled={disabled}
          aria-label={`${label} swatch`}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
        />
        <Input
          id={id}
          value={value}
          maxLength={7}
          disabled={disabled}
          className="mono"
          onChange={(e) => onChange(e.target.value.toUpperCase())}
        />
      </div>
      <span className="faint" style={{ fontSize: 11 }}>
        {hint}
      </span>
    </div>
  );
}

/** One editable set of footer links. Same control for socials and legal, different ceilings. */
function LinkRows({
  title,
  max,
  rows,
  disabled,
  onChange,
}: {
  title: string;
  max: number;
  rows: readonly LinkRow[];
  disabled: boolean;
  onChange: (next: LinkRow[]) => void;
}): JSX.Element {
  return (
    <div className="field">
      <span className="field__label">
        {title} (up to {max})
      </span>
      <p className="faint" style={{ fontSize: 12, marginTop: -2 }}>
        Addresses must start with <code className="mono">https://</code>. A plain link in a
        message sent to everyone is one a network attacker rewrites for all of them at once.
      </p>
      <div className="footer-links">
        {rows.map((row, index) => (
          <div className="footer-link" key={index}>
            <Input
              value={row.label}
              maxLength={40}
              disabled={disabled}
              placeholder="Label"
              aria-label={`${title} ${String(index + 1)} label`}
              onChange={(e) =>
                onChange(rows.map((r, i) => (i === index ? { ...r, label: e.target.value } : r)))
              }
            />
            <Input
              value={row.url}
              disabled={disabled}
              placeholder="https://…"
              aria-label={`${title} ${String(index + 1)} address`}
              onChange={(e) =>
                onChange(rows.map((r, i) => (i === index ? { ...r, url: e.target.value } : r)))
              }
            />
            <button
              type="button"
              className="footer-link__x"
              aria-label={`Remove ${title} ${String(index + 1)}`}
              disabled={disabled}
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
            >
              <IconX size={14} />
            </button>
          </div>
        ))}
        {rows.length === 0 && <div className="faint" style={{ fontSize: 12 }}>None yet.</div>}
      </div>
      <Button
        size="sm"
        variant="ghost"
        disabled={disabled || rows.length >= max}
        onClick={() => onChange([...rows, { label: '', url: '' }])}
      >
        Add a link
      </Button>
    </div>
  );
}
